import type { Carer } from '@lmc/ts-common'
import { Button, PageHeader } from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { RiskAssessmentsSection } from 'components/carePlans/RiskAssessmentsSection'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import Operations from 'operations'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ConsentSection from 'screens/CarePlans/screens/CarePlanReviewScreen/components/ConsentSection/ConsentSection'
import {
  ButtonsWrapper,
  ProfileContent,
  ProfileWrapper
} from '../../styledComponents'
import { scrollToTop } from '../../utils/utils'
import ContingencyPlanForm from './components/ContingencyPlanForm'
import LinkRiskAssessmentsModal from './components/LinkRiskAssessmentsModal'
import type { RiskAssessmentOption } from './types'

const AddContingencyPlan = () => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [isCreateLoading, setIsCreateLoading] = useState(false)
  const [consentFormIds, setConsentFormIds] = useState<string[]>([])
  const [riskAssessmentIds, setRiskAssessmentIds] = useState<string[]>([])
  const [pendingRiskAssessmentIds, setPendingRiskAssessmentIds] = useState<
    string[]
  >([])
  const [isRiskModalVisible, setIsRiskModalVisible] = useState(false)
  const { id } = useParams()
  const dispatch = useDispatch()
  const featureFlags = useFeatureFlags({
    flagNames: [
      'informed_consent',
      'consent_future_plan',
      'risk_assessment_future_plan'
    ]
  }).data
  const isInformedConsentEnabled =
    featureFlags['informed_consent']?.enabled || false
  const isConsentFuturePlanEnabled =
    featureFlags['consent_future_plan']?.enabled || false
  const isRiskAssessmentFuturePlanEnabled =
    featureFlags['risk_assessment_future_plan']?.enabled || false
  const riskAssessments = useSelector(
    (state: { data: Record<string, unknown[]> }) =>
      state.data['risk-assessments']
  )
  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const handleOnCancel = () => {
    navigate(`../`, { relative: 'path' })
  }

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  useEffect(() => {
    if (!id || !isRiskAssessmentFuturePlanEnabled) return
    dispatch(
      Operations.loadList('risk-assessments', {
        resident: id,
        mostRecent: true
      })
    )
  }, [id, dispatch, isRiskAssessmentFuturePlanEnabled])

  const normalizedRiskAssessments = useMemo(
    () =>
      (riskAssessments || []).reduce<
        Array<{
          id: string
          name: string
          form?: { name?: string }
          reviewedOn?: string
        }>
      >((acc, riskAssessment) => {
        const item = riskAssessment as {
          id?: string
          name?: string
          form?: { name?: string }
          reviewedOn?: string
        }

        if (!item.id) return acc

        acc.push({
          id: item.id,
          name: item.name || item.form?.name || 'Untitled',
          form: item.form,
          reviewedOn: item.reviewedOn
        })
        return acc
      }, []),
    [riskAssessments]
  )

  const riskAssessmentOptions: RiskAssessmentOption[] = useMemo(
    () =>
      normalizedRiskAssessments.map((item) => ({
        id: item.id,
        name: item.name,
        reviewedOn: item.reviewedOn
      })),
    [normalizedRiskAssessments]
  )

  const riskAssessmentsFields = useMemo(
    () => ({
      id: null,
      editedOn: null,
      riskAssessments: riskAssessmentIds,
      reviewedAssessments: [],
      populatedRiskAssessments: normalizedRiskAssessments
    }),
    [riskAssessmentIds, normalizedRiskAssessments]
  )

  const openRiskModal = () => {
    setPendingRiskAssessmentIds(riskAssessmentIds)
    setIsRiskModalVisible(true)
  }

  const handleRiskModalConfirm = () => {
    setRiskAssessmentIds(pendingRiskAssessmentIds)
    setIsDirty(true)
    setIsRiskModalVisible(false)
  }

  const handleRiskModalCancel = () => {
    setPendingRiskAssessmentIds([])
    setIsRiskModalVisible(false)
  }

  const handleSetRiskAssessmentField = ({
    key,
    value
  }: {
    key: string
    value: string[]
  }) => {
    if (key !== 'riskAssessments') return
    setRiskAssessmentIds(value)
    setIsDirty(true)
  }

  return (
    <ProfileWrapper ref={topRef}>
      <PageHeader sticky title={t(supConstants.FUTURE_PLAN)}>
        <ButtonsWrapper>
          <Button onClick={handleOnCancel}>{t(supConstants.CANCEL)}</Button>
          <Button
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isCreateLoading}
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent>
        <NavigationModal canNavigate={!isDirty} />
        <ContingencyPlanForm
          create
          form={form}
          consentFormIds={consentFormIds}
          riskAssessmentIds={riskAssessmentIds}
          isConsentFuturePlanEnabled={isConsentFuturePlanEnabled}
          isRiskAssessmentFuturePlanEnabled={isRiskAssessmentFuturePlanEnabled}
          setIsCreateLoading={setIsCreateLoading}
          setIsDirty={setIsDirty}
        >
          {!!id && isRiskAssessmentFuturePlanEnabled && (
            <RiskAssessmentsSection
              readOnly={false}
              compactSpacing
              carers={carers}
              fields={riskAssessmentsFields}
              riskAssessments={normalizedRiskAssessments}
              router={{ params: { id }, navigate }}
              setFormField={handleSetRiskAssessmentField}
              onOpenModal={openRiskModal}
              carePlanNominative="Future Plan"
            />
          )}
          {!!id && isInformedConsentEnabled && isConsentFuturePlanEnabled && (
            <ConsentSection
              residentId={id}
              value={consentFormIds}
              onChange={(selectedIds) => {
                setConsentFormIds(selectedIds)
                setIsDirty(true)
              }}
            />
          )}
        </ContingencyPlanForm>
      </ProfileContent>
      <LinkRiskAssessmentsModal
        isModalVisible={isRiskModalVisible}
        setIsModalVisible={setIsRiskModalVisible}
        pendingRiskAssessmentIds={pendingRiskAssessmentIds}
        setPendingRiskAssessmentIds={setPendingRiskAssessmentIds}
        riskAssessmentOptions={riskAssessmentOptions}
        onConfirm={handleRiskModalConfirm}
        onCancel={handleRiskModalCancel}
      />
    </ProfileWrapper>
  )
}

export default AddContingencyPlan
